/**
 * Navigation-away-from-a-draft state, shared across scenario entry points.
 *
 * Split from `scenarioStore` deliberately: this module pulls no GraphQL, so
 * suites that mock the scenario store wholesale still exercise the real guard
 * below rather than a hand-copied stand-in.
 * @module features/squad-planning/stores/scenarioNavStore
 */

import { makeVar } from "@apollo/client";

import { requestCreateScenarioModal } from "@/features/squad-planning/stores/scenarioStore";

// The entry scope lives in its own module (no import of the scenario store)
// so scenarioStore can sync it without a dependency cycle; re-exported here
// so existing consumers keep this import path.
export {
  scenarioEntryScopeVar,
  setScenarioEntryScope,
  clearScenarioEntryScope,
  syncScenarioEntryScopeToActive,
  useScenarioEntryScope,
} from "./scenarioEntryScopeStore";
export type { ScenarioEntryScope } from "./scenarioEntryScopeStore";

/**
 * What the user is trying to do while an unsaved draft is in flight.
 * `current` = go back to Current Squad, `scenario` = switch to a saved
 * scenario, `create` = start a brand-new scenario. All three discard the
 * draft, so all three go through the same confirm-discard dialog (SE-5564).
 */
export type PendingNavTarget =
  | { readonly kind: "current" }
  | { readonly kind: "scenario"; readonly id: string }
  | { readonly kind: "create" }
  | null;

/**
 * Pending navigation away from an in-flight draft. Shared rather than local to
 * the pill bar because the mobile create affordance is a screen-level FAB that
 * lives outside it — both entry points must land in the same confirm-discard
 * dialog, which the pill bar container still owns and renders.
 */
export const pendingNavTargetVar = makeVar<PendingNavTarget>(null);

/**
 * Opens the Create Scenario modal, asking first when it would destroy a draft.
 * @param isPlanningModeActive - Whether planning mode is currently active
 * @param draftActionCount - Number of unsaved actions in the current draft
 * @remarks The single guard for creating a scenario (SE-5564): without it,
 * starting a new scenario mid-draft hard-reset the draft with no prompt and
 * silently emptied the scenario the user went on to create.
 */
export function openCreateScenario(
  isPlanningModeActive: boolean,
  draftActionCount: number
): void {
  if (isPlanningModeActive && draftActionCount > 0) {
    pendingNavTargetVar({ kind: "create" });
    return;
  }
  requestCreateScenarioModal();
}
